import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import services from '../../services';

// Share a ticket with other boards/lists (task_board_shares). The ticket stays on its own board and
// list; on the shared board it shows too. Which boards/lists a user may pick comes from the server
// (admins: all; others: their "Ticket sharing" setting).
export const useTicketShares = (taskId, homeBoardId) => {
    const [shares, setShares] = useState([]);
    const [targets, setTargets] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        if (!taskId) return;
        services.taskShare
            .GET(taskId)
            .then((res) => setShares(res?.data?.data || []))
            .catch(() => setShares([]));
    }, [taskId]);

    useEffect(load, [load]);

    useEffect(() => {
        services.taskShare
            .TARGETS()
            .then((res) => setTargets(res?.data?.data || []))
            .catch(() => setTargets([]));
    }, []);

    const share = async (boardId, boardListId) => {
        if (!taskId) {
            toast.error('Please select a board first');
            return;
        }
        setBusy(true);
        try {
            const res = await services.taskShare.SHARE(taskId, { boardId, boardListId });
            setShares(res?.data?.data || []);
            toast.success('Ticket shared — it stays on this board too');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not share the ticket');
        } finally {
            setBusy(false);
        }
    };

    const unshare = async (shareId) => {
        setBusy(true);
        try {
            const res = await services.taskShare.UNSHARE(shareId);
            setShares(res?.data?.data || []);
            toast.success('Share removed');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not remove the share');
        } finally {
            setBusy(false);
        }
    };

    const visibleTargets = targets.filter((b) => b.boardId !== homeBoardId);
    return { shares, targets: visibleTargets, busy, share, unshare };
};

// "Shared with: Board / List ×" under the board button
export const ShareChips = ({ shares, busy, onRemove }) => {
    if (!shares?.length) return null;
    return (
        <div className="flex flex-wrap items-center gap-1 pb-[0.5vw]" data-testid="ticket-shares">
            <span className="text-gray-500" style={{ fontSize: 11 }}>
                Shared with:
            </span>
            {shares.map((s) => (
                <span
                    key={s.id}
                    className="flex items-center gap-1 rounded border px-1"
                    style={{ fontSize: 11, borderColor: '#cfe8d6', background: '#f3faf5' }}
                    title={[
                        s.sharedBy?.name ? `Shared by ${s.sharedBy.name}` : '',
                        s.canRemove ? '' : 'Only an admin, the person who shared it, or a manager / user with "Remove share" switched on for this ticket\'s own board (User management → Assign board) can remove it',
                    ].filter(Boolean).join('. ') || undefined}
                >
                    {s.boardName} / {s.listName}
                    {s.canRemove && <button
                        type="button"
                        aria-label={`Stop sharing with ${s.boardName}`}
                        disabled={busy}
                        onClick={() => onRemove(s.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '0 2px', lineHeight: 1 }}
                    >
                        ×
                    </button>}
                </span>
            ))}
        </div>
    );
};

// Section inside the board dropdown: boards/lists the ticket can be shared to
export const ShareMenuSection = ({ targets, shares, searchTerm, busy, onShare }) => {
    if (!targets?.length) return null;
    const term = (searchTerm || '').toLowerCase();
    const sharedList = new Set((shares || []).map((s) => s.boardListId));
    const boards = targets
        .map((b) => ({
            ...b,
            lists: b.lists.filter((l) => !term || b.boardName?.toLowerCase().includes(term) || l.name?.toLowerCase().includes(term)),
        }))
        .filter((b) => b.lists.length);
    if (!boards.length) return null;
    return (
        <div data-testid="share-menu">
            <li
                className="px-4 pt-3 pb-1 text-left"
                style={{ fontSize: 11, fontWeight: 700, color: '#1a7d3e', borderTop: '2px solid #e5e7eb', listStyle: 'none' }}
            >
                SHARE WITH ANOTHER BOARD
                <div style={{ fontWeight: 400, color: '#6b7280', textTransform: 'none' }}>The ticket also stays on its own board and list</div>
            </li>
            {boards.map((b) => (
                <div key={b.boardId}>
                    <li className="uppercase text-[11px] text-black font-bold px-4 pt-2 pb-1 text-left" style={{ listStyle: 'none' }}>
                        {b.boardName}
                    </li>
                    {b.lists.map((l) => (
                        <li
                            key={l.id}
                            onClick={() => !busy && !sharedList.has(l.id) && onShare(b.boardId, l.id)}
                            className="px-4 py-2 hover:bg-gray-100 text-gray-800 cursor-pointer text-sm text-left"
                            style={{ listStyle: 'none', opacity: sharedList.has(l.id) ? 0.5 : 1 }}
                        >
                            ↗ {l.name}
                            {sharedList.has(l.id) ? ' (shared)' : ''}
                        </li>
                    ))}
                </div>
            ))}
        </div>
    );
};
